<template>
	<v-card flat outlined>
		<v-container fluid class="pb-0">
			<v-row>
				<v-col cols="12" lg="12" class="pb-0 pt-0">
					<v-card flat class="pb-0 pt-0">
						<v-card-text class="pb-0 pt-0">
							<v-row class="d-flex justify-space-between">
								<v-col class="pb-0 pt-0">
									<v-card-title class="justify-left align-center pa-2">
										{{ $t('titles.schedule') }}</v-card-title
									>
								</v-col>
								<v-col class="d-flex justify-end align-center pa-2 pb-0 pt-0">
									<v-btn color="basicButton" @click="copySchedule()" class="mr-2">
										{{ $t('buttons.copySchedule') }}
										<v-icon small class="ml-2">fas fa-copy</v-icon>
									</v-btn>
									<v-btn color="basicButton" @click="exportCandidateSigningList()" class="mr-2">
										{{ $t('buttons.candidateSigningList') }}
										<v-icon small class="ml-2">fas fa-file</v-icon>
									</v-btn>
									<v-btn
										color="basicButton"
										@click="exportTeacherSigningList()"
										v-if="scheduleTable.total > 0"
									>
										{{ $t('buttons.teacherSigningList') }}
										<v-icon small class="ml-2">fas fa-file</v-icon>
									</v-btn>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<TheTable
										:additional-filters="scheduleTable.additionalFilters"
										:default-sort="scheduleTable.defaultSort"
										:excel="canEdit"
										:fields="scheduleTable.headers"
										:header-selection-key="'schedule-headers'"
										:height="scheduleTable.height"
										:total-records="scheduleTable.total"
										@excel="excelExport"
										@headers="selectHeaders"
										@reload="reloadData"
									>
										<tr
											v-for="(item, key) in scheduleTable.items"
											:key="key"
											:class="{ 'cursor-pointer': true }"
										>
											<td v-if="scheduleTable.selectedHeaders.includes('date')">
												{{ item.date != null ? item.date.split('-').reverse().join('.') : '' }}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('start_time')">
												{{ extractTimeFromDateTime(item.start_time) }}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('end_time')">
												{{ extractTimeFromDateTime(item.end_time) }}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('duration')">
												{{ appointmentDuration(item.start_time, item.end_time) }}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('office_id')">
												{{ item.planned_course.office.name }}
											</td>
											<td
												v-if="scheduleTable.selectedHeaders.includes('appointment_location_id')"
											>
												{{
													item.appointment_location.classroom_id
														? item.appointment_location.classroom.name
														: item.appointment_location.ship.name
												}}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('course_module_id')">
												{{ item.course_module.name }}
											</td>
											<td v-if="scheduleTable.selectedHeaders.includes('teacher_id')">
												{{ item.teacher.full_name }}
											</td>
										</tr>
										<tr v-if="totalDuration != null">
											<td></td>
											<td></td>
											<td>
												{{ sum }}
											</td>
											<td>
												{{ totalDuration }}
											</td>
										</tr>
									</TheTable>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
			<Modal :show-modal="showModal">
				<CopyScheduleFrom :courseId="courseId" @close="closeModal"></CopyScheduleFrom>
			</Modal>
		</v-container>
	</v-card>
</template>

<script>
import TheTable from '../../trinkets/TheTable';
import Modal from '../../trinkets/Modal';
import CopyScheduleFrom from './CopyScheduleFrom';

export default {
	name: 'Schedule',
	components: { TheTable, Modal, CopyScheduleFrom },
	props: {
		courseId: [String, Number],
		canEdit: Boolean,
	},
	emits: ['reloadParticipants'],
	data: function () {
		return {
			scheduleTable: {
				selectedHeaders: [],
				headers: [
					{ text: this.$t('fields.date'), value: 'date', type: '', allowFilter: true },
					{
						text: this.$t('fields.startAppointmentTime'),
						value: 'start_time',
						type: '',
						allowFilter: true,
					},
					{
						text: this.$t('fields.endAppointmentTime'),
						value: 'end_time',
						type: '',
						sortable: false,
						allowFilter: true,
					},
					{
						text: this.$t('fields.duration'),
						value: 'duration',
						type: '',
						sortable: false,
						allowFilter: false,
					},
					{
						text: this.$t('fields.office'),
						value: 'office_id',
						type: '',
						sortable: false,
						allowFilter: true,
					},
					{
						text: this.$t('fields.appointmentLocation'),
						value: 'appointment_location_id',
						type: '',
						sortable: false,
						allowFilter: true,
					},
					{
						text: this.$t('fields.module'),
						value: 'course_module_id',
						type: '',
						sortable: false,
						allowFilter: false,
					},
					{
						text: this.$t('fields.teacher'),
						value: 'teacher_id',
						type: '',
						sortable: false,
						allowFilter: true,
					},
				],
				additionalFilters: [],
				items: [],
				total: 0,
				lastRequest: null,
				height: '0px',
				defaultSort: {
					field: 'start_time',
					descending: false,
				},
			},
			totalDuration: null,
			sum: 'Sum: ',
			showModal: false,
		};
	},
	methods: {
		selectHeaders(headers) {
			this.scheduleTable.selectedHeaders = headers;
		},
		copySchedule() {
			this.showModal = true;
		},
		closeModal() {
			this.showModal = false;
			this.costId = 0;
		},
		extractTimeFromDateTime(dateTime) {
			const date = new Date(dateTime);

			if (isNaN(date)) {
				throw new Error('Invalid date-time string');
			}

			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');

			return `${hours}:${minutes}`;
		},
		appointmentDuration(startTime, endTime) {
			const startDateTime = new Date(startTime);
			const endDateTime = new Date(endTime);

			const durationInMinutes = Math.abs((endDateTime - startDateTime) / (60 * 1000));

			const numberOfHours = Math.floor(durationInMinutes / 45);
			const numberOfMinutes = durationInMinutes % 45;

			let decimalPart = '00';
			const minutes = numberOfMinutes / 45;
			const stringNumber = String(Math.round(minutes * 100) / 100);

			if (stringNumber.includes('.')) {
				decimalPart = stringNumber.split('.')[1];
			}

			const result = `${numberOfHours}.${decimalPart}`;
			return result;
		},
		reloadData(request) {
			this.scheduleTable.lastRequest = request;
			let vm = this;
			this.$APICall(
				'get',
				'planned-course-schedule/' + this.$route.params.id,
				request,
				(data, success) => {
					if (success) {
						vm.scheduleTable.items = data.items;
						vm.scheduleTable.total = data.total;
						vm.totalDuration = data.totalDuration;
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		resolveWindowResize() {
			this.scheduleTable.height = window.innerHeight - 330 + 'px';
		},
		exportCandidateSigningList() {
			let vm = this;
			this.$APICall(
				'get',
				'excelCandidates/planned-courses/' + this.$route.params.id,
				null,
				(data, success) => {
					if (success) {
						window.open(data.link);
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		exportTeacherSigningList() {
			let vm = this;
			this.$APICall(
				'get',
				'excelTeachers/planned-courses/' + this.$route.params.id,
				null,
				(data, success) => {
					if (success) {
						window.open(data.link);
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		excelExport() {
			let excelRequest = JSON.parse(JSON.stringify(this.scheduleTable.lastRequest));
			excelRequest.pagination.itemsPerPage = 999999999;
			excelRequest.pagination.page = 1;

			let vm = this;
			this.$APICall(
				'get',
				'planned-course-schedule/' + this.$route.params.id,
				excelRequest,
				(data, success) => {
					if (success) {
						vm.$APICall('post', 'excel/schedule', data.items, (data, success) => {
							window.open(data.link);
						});
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
	},
	mounted() {
		let vm = this;
		this.$modalBus.$on('reloadSchedule', () => {
			vm.reloadData(vm.scheduleTable.lastRequest);
		});

		window.addEventListener('resize', this.resolveWindowResize);
		this.resolveWindowResize();
	},
};
</script>

<style scoped></style>
